import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import type { IssueBoardState, ProjectKind } from '@aiai/shared';
import { ISSUE_BOARD_PAGE_SIZE } from '@aiai/shared';
import {
  bulkIssueAction,
  fetchProject,
  fetchProjectIssuesBoard,
} from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { newIssueHref } from '../lib/projectNav';
import { EmptyState, ErrorState, LoadingState } from './States';
import { OverflowMenu } from './OverflowMenu';
import { Spinner } from './Spinner';
import { Callout } from './Callout';
import { RowList } from './Row';
import { SegmentedTabs } from './Tabs';
import { ConfirmDialog } from './ConfirmDialog';
import { useDebounce } from '../hooks/useDebounce';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { useCapabilities } from '../hooks/useCapabilities';
import { IssueRow } from './issues/IssueRow';
import { hasActiveRun } from '../lib/issueRuns';

type LoopStatusFilter = IssueBoardState;

const ALL_TAGS = 'all';

/**
 * The project's loop issues — the ones the pipeline can run against. GitHub
 * repo issues are pulled in separately via the "Migrate GitHub issues" dialog
 * (a one-time setup step), so this panel stays focused on loop issues.
 */
export function IssuesPanel({
  projectId,
  kind = 'repo',
  projectRef,
  onSelectionModeChange,
  initialStatus = 'all',
}: {
  /** Canonical UUID — used for all board data fetches. */
  projectId: string;
  kind?: ProjectKind;
  /** Pretty handle (code) for building navigation links; defaults to projectId. */
  projectRef?: string;
  /**
   * Fires when bulk selection starts/ends. Pages use it to yield the bottom
   * edge on phones (the sticky bulk bar and the New-issue FAB share it).
   */
  onSelectionModeChange?: (selecting: boolean) => void;
  /** The board filter to open on (a planet-page deep link); remount to re-apply. */
  initialStatus?: IssueBoardState;
}) {
  const queryClient = useQueryClient();
  const { wp } = useWorkspacePath();
  const isPrototype = kind === 'prototype';
  const linkRef = projectRef ?? projectId;
  // A `viewer` grant reads the board but holds no issue-board capabilities —
  // the create entry point must not appear for them (a `contributor` grant,
  // or org owner, does hold issue:create).
  const { can } = useCapabilities();
  const canCreateIssue = can('issue:create', { projectId });
  const [loopStatusFilter, setLoopStatusFilter] = useState<LoopStatusFilter>(initialStatus);
  const [tagFilter, setTagFilter] = useState<string>(ALL_TAGS);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchTerm.trim(), 300);
  const q = debouncedSearch.length >= 2 ? debouncedSearch : '';

  const boardQuery = useQuery({
    queryKey: qk.projectIssuesBoard(projectId, {
      status: loopStatusFilter,
      tag: tagFilter === ALL_TAGS ? '' : tagFilter,
      q,
      page,
    }),
    queryFn: () =>
      fetchProjectIssuesBoard(projectId, {
        status: loopStatusFilter,
        tag: tagFilter === ALL_TAGS ? undefined : tagFilter,
        q: q || undefined,
        page,
        pageSize: ISSUE_BOARD_PAGE_SIZE,
      }),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });

  // Project autonomy dial — drives AssigneeChip for `open` and `proposed` issues.
  // Undefined while loading → chip falls back to historical defaults.
  const autonomyQuery = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
    enabled: !!projectId,
  });
  const autonomy = autonomyQuery.data?.autonomy;

  const bulk = useMutation({
    mutationFn: bulkIssueAction,
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      // Keep only skipped (active-run) issues selected so they stay visible
      const skippedIds = new Set(results.filter((r) => r.skipped).map((r) => r.id));
      setSelectedIds(skippedIds);
      if (skippedIds.size === 0) {
        setConfirmBulkDelete(false);
      }
    },
  });

  // The server owns sort, filter, paging, counts, and the tag set now — the
  // panel just renders the page it's handed.
  const board = boardQuery.data;
  const pagedIssues = board?.items ?? [];
  const counts = board?.counts ?? {
    all: 0,
    proposed: 0,
    open: 0,
    building: 0,
    review: 0,
    failed: 0,
    closed: 0,
    mine: 0,
  };
  const allTags = board?.tags ?? [];
  const total = board?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / ISSUE_BOARD_PAGE_SIZE));
  const isSearchActive = q.length >= 2;

  // Issues eligible for selection (active runs excluded) — only current page
  const selectableIssues = pagedIssues.filter((i) => !hasActiveRun(i));

  const T = (v: LoopStatusFilter, base: string, n: number) => ({
    value: v,
    label: n > 0 ? `${base} (${n})` : base,
  });
  const statusFilterOptions: Array<{ value: LoopStatusFilter; label: string }> = [
    T('mine', 'Needs you', counts.mine),
    T('all', 'All', counts.all),
    T('proposed', 'Proposed', counts.proposed),
    T('open', 'Open', counts.open),
    T('building', 'Building', counts.building),
    T('review', 'Review', counts.review),
    T('failed', 'Failed', counts.failed),
    T('closed', 'Closed', counts.closed),
  ];

  // Auto-reset to page 0 when the current page falls out of range (e.g. after
  // bulk-delete empties the last page). totalPages is always >= 1, so this
  // only fires when page > 0 and the server returned fewer pages than expected.
  useEffect(() => {
    if (page > 0 && page >= totalPages) setPage(0);
  }, [page, totalPages]);

  // Keep the indeterminate state of the "select all" checkbox in sync
  useEffect(() => {
    if (selectAllRef.current) {
      const allSelectable = selectableIssues.length;
      const nSelected = selectedIds.size;
      selectAllRef.current.indeterminate = nSelected > 0 && nSelected < allSelectable;
    }
  }, [selectedIds.size, selectableIssues.length]);

  const selectionMode = selectedIds.size > 0;

  useEffect(() => {
    onSelectionModeChange?.(selectionMode);
  }, [selectionMode, onSelectionModeChange]);

  // Unmounting (tab switch) must not leave the parent thinking selection mode is on.
  const selectionModeCb = useRef(onSelectionModeChange);
  selectionModeCb.current = onSelectionModeChange;
  useEffect(() => () => selectionModeCb.current?.(false), []);

  function handleToggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleSelectAll() {
    if (selectedIds.size === selectableIssues.length && selectableIssues.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIssues.map((i) => i.code ?? i.id)));
    }
  }

  function clearSelection() {
    setSelectedIds(new Set());
    bulk.reset();
  }

  const selectedCount = selectedIds.size;
  const selectedLabel = `${selectedCount} issue${selectedCount !== 1 ? 's' : ''}`;

  // IDs of selected issues whose state is 'failed' — used by the Retry button.
  const failedSelectedIds = pagedIssues
    .filter((i) => selectedIds.has(i.code ?? i.id) && i.state === 'failed')
    .map((i) => i.code ?? i.id);

  return (
    <div>
      {counts.all > 0 && (
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint"
              aria-hidden="true"
            />
            <input
              type="search"
              className="input h-8 w-full pl-8 text-sm"
              placeholder="Search issues…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              aria-label="Search issues by title, body, or tag"
            />
          </div>
          {allTags.length > 0 && (
            <select
              className="input h-8 w-auto max-w-[9rem] flex-none py-0 text-xs"
              value={tagFilter}
              onChange={(e) => { setTagFilter(e.target.value); setPage(0); clearSelection(); }}
              aria-label="Filter issues by tag"
            >
              <option value={ALL_TAGS}>All tags</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <p className="mb-3 text-xs text-ink-faint">
        {isPrototype
          ? 'Issues authored here. Open one and click Run to start the agent pipeline on the prototype.'
          : 'Issues pulled in from GitHub or authored here. Open one and click Run to start the agent pipeline.'}
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        {pagedIssues.length > 0 && (
          <label className="flex flex-none cursor-pointer items-center gap-2">
            <input
              ref={selectAllRef}
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-line accent-signal"
              checked={
                selectableIssues.length > 0 &&
                selectedIds.size === selectableIssues.length
              }
              onChange={handleSelectAll}
              aria-label={
                selectedIds.size === selectableIssues.length && selectableIssues.length > 0
                  ? 'Deselect all'
                  : 'Select all visible issues'
              }
            />
            <span className="text-xs font-medium text-ink-muted">
              {selectionMode
                ? `${selectedIds.size} of ${selectableIssues.length} selected`
                : 'Select all'}
            </span>
          </label>
        )}
        <SegmentedTabs
          value={loopStatusFilter}
          onChange={(v) => { setLoopStatusFilter(v); setPage(0); clearSelection(); }}
          options={statusFilterOptions}
          ariaLabel="Filter issues by status"
        />
      </div>

      {boardQuery.isLoading ? (
          <LoadingState label="Loading issues…" />
        ) : boardQuery.isError ? (
          <ErrorState
            message={errorMessage(boardQuery.error, 'Failed to load issues')}
            onRetry={() => boardQuery.refetch()}
          />
        ) : counts.all === 0 ? (
          <EmptyState
            compact
            title="No issues yet"
            description={
              isPrototype
                ? 'Write a markdown brief, then use Save & run to start the agent pipeline on the prototype.'
                : 'Write a markdown brief and use Save & run, or migrate an issue from GitHub via the ⋯ menu.'
            }
            action={
              canCreateIssue ? (
                <Link to={wp(newIssueHref(kind, linkRef))} className="btn-primary">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  New issue
                </Link>
              ) : undefined
            }
          />
        ) : isSearchActive && pagedIssues.length === 0 ? (
          <EmptyState
            compact
            title={`No issues match "${debouncedSearch}"`}
            description="Try a different search term, or clear the search to see all issues."
          />
        ) : pagedIssues.length === 0 ? (
          <EmptyState
            compact
            title={
              loopStatusFilter === 'mine'
                ? 'Nothing needs you'
                : loopStatusFilter === 'proposed'
                ? 'No issues awaiting approval'
                : loopStatusFilter === 'open'
                ? 'No open issues'
                : loopStatusFilter === 'building'
                ? 'No issues building'
                : loopStatusFilter === 'review'
                ? 'No issues in review'
                : loopStatusFilter === 'failed'
                ? 'No failed issues'
                : loopStatusFilter === 'closed'
                ? 'No closed issues'
                : 'No issues'
            }
            description={
              loopStatusFilter === 'mine'
                ? 'No plans to approve, PRs to merge, or failed runs — the agents are handling it.'
                : loopStatusFilter === 'proposed'
                ? 'No loop proposals are waiting for your approval right now.'
                : loopStatusFilter === 'open'
                ? "All issues are closed. Switch to 'All' to see the full history."
                : loopStatusFilter === 'building'
                ? 'No agents are currently running against this project.'
                : loopStatusFilter === 'review'
                ? 'No pull requests are waiting for your review right now.'
                : loopStatusFilter === 'failed'
                ? 'Nothing needs attention — no runs have failed.'
                : loopStatusFilter === 'closed'
                ? 'No issues have been closed yet.'
                : undefined
            }
          />
        ) : (
          <>
            <RowList>
              {pagedIssues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  selected={selectedIds.has(issue.code ?? issue.id)}
                  onSelect={handleToggle}
                  autonomy={autonomy}
                />
              ))}
            </RowList>

            {totalPages > 1 && (
              <div className="mt-3 flex items-center justify-center gap-3">
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => { setPage((p) => p - 1); setSelectedIds(new Set()); }}
                  disabled={page === 0}
                >
                  Previous
                </button>
                <span className="text-xs text-ink-muted">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => { setPage((p) => p + 1); setSelectedIds(new Set()); }}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </button>
              </div>
            )}

            {/* Bulk action bar — sticky at the bottom when rows are selected.
                On phones it docks above the bottom section bar and collapses to
                one row: count + Run + the rest behind a ⋯ menu. */}
            {selectionMode && (
              <div className="sticky bottom-16 mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-2.5 shadow-lg sm:bottom-0 sm:px-4">
                <span className="text-sm font-medium text-ink">{selectedLabel}</span>
                <button
                  type="button"
                  className="btn-ghost btn-sm min-h-[2.25rem] sm:order-last sm:ml-auto sm:min-h-0"
                  onClick={clearSelection}
                  aria-label="Clear selection"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                  Clear
                </button>
                <span className="flex-1 sm:hidden" aria-hidden="true" />
                <div className="hidden flex-wrap items-center gap-2 sm:flex">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={bulk.isPending}
                    onClick={() =>
                      bulk.mutate({ ids: [...selectedIds], action: 'complete' })
                    }
                  >
                    {bulk.isPending && bulk.variables?.action === 'complete' && (
                      <Spinner className="h-3.5 w-3.5" />
                    )}
                    Mark done
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={bulk.isPending}
                    onClick={() =>
                      bulk.mutate({ ids: [...selectedIds], action: 'close' })
                    }
                  >
                    {bulk.isPending && bulk.variables?.action === 'close' && (
                      <Spinner className="h-3.5 w-3.5" />
                    )}
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn-primary btn-sm"
                    disabled={bulk.isPending}
                    onClick={() =>
                      bulk.mutate({ ids: [...selectedIds], action: 'run' })
                    }
                  >
                    {bulk.isPending && bulk.variables?.action === 'run' ? (
                      <Spinner className="h-3.5 w-3.5" />
                    ) : (
                      <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Run selected
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={bulk.isPending || failedSelectedIds.length === 0}
                    title={
                      failedSelectedIds.length === 0
                        ? 'Select at least one failed issue to retry'
                        : `Retry ${failedSelectedIds.length} failed issue${failedSelectedIds.length !== 1 ? 's' : ''}`
                    }
                    onClick={() =>
                      bulk.mutate({ ids: failedSelectedIds, action: 'run' })
                    }
                  >
                    {bulk.isPending && bulk.variables?.action === 'run' &&
                    bulk.variables?.ids.every((id) => failedSelectedIds.includes(id)) ? (
                      <Spinner className="h-3.5 w-3.5" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Retry failed
                  </button>
                  <button
                    type="button"
                    className="btn-danger-ghost btn-sm"
                    disabled={bulk.isPending}
                    onClick={() => { bulk.reset(); setConfirmBulkDelete(true); }}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Delete
                  </button>
                </div>
                <div className="flex items-center gap-2 sm:hidden">
                  <button
                    type="button"
                    className="btn-primary min-h-[2.5rem]"
                    disabled={bulk.isPending}
                    onClick={() =>
                      bulk.mutate({ ids: [...selectedIds], action: 'run' })
                    }
                  >
                    {bulk.isPending && bulk.variables?.action === 'run' ? (
                      <Spinner className="h-3.5 w-3.5" />
                    ) : (
                      <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Run
                  </button>
                  <OverflowMenu
                    label="More bulk actions"
                    items={[
                      {
                        label: 'Mark done',
                        disabled: bulk.isPending,
                        onClick: () =>
                          bulk.mutate({ ids: [...selectedIds], action: 'complete' }),
                      },
                      {
                        label: 'Close',
                        disabled: bulk.isPending,
                        onClick: () =>
                          bulk.mutate({ ids: [...selectedIds], action: 'close' }),
                      },
                      {
                        label:
                          failedSelectedIds.length > 0
                            ? `Retry ${failedSelectedIds.length} failed`
                            : 'Retry failed',
                        disabled: bulk.isPending || failedSelectedIds.length === 0,
                        onClick: () =>
                          bulk.mutate({ ids: failedSelectedIds, action: 'run' }),
                      },
                      {
                        label: 'Delete…',
                        danger: true,
                        disabled: bulk.isPending,
                        onClick: () => { bulk.reset(); setConfirmBulkDelete(true); },
                      },
                    ]}
                  />
                </div>
                {bulk.isError && (
                  <div className="w-full">
                    <Callout tone="danger">
                      {errorMessage(bulk.error, 'Bulk action failed')}
                    </Callout>
                  </div>
                )}
              </div>
            )}
          </>
        )}

      <ConfirmDialog
        open={confirmBulkDelete}
        title={`Delete ${selectedLabel}?`}
        confirmLabel={`Delete ${selectedLabel}`}
        busy={bulk.isPending}
        error={bulk.isError ? errorMessage(bulk.error, 'Could not delete issues') : null}
        onConfirm={() => bulk.mutate({ ids: [...selectedIds], action: 'delete' })}
        onClose={() => { setConfirmBulkDelete(false); bulk.reset(); }}
        message={
          <>
            Delete{' '}
            <span className="font-medium text-ink">{selectedLabel}</span>
            {' '}and their run history? This cannot be undone.
          </>
        }
      />
    </div>
  );
}
